"use client";

import { useEffect, useMemo, useRef } from "react";
export type MiniAudioVisualizerProps = {
  stream: MediaStream | null;
  isActive: boolean;
  className?: string;
  widthPx?: number;
  heightPx?: number;
  mode?: "single" | "dual";
  lowCutoffHz?: number;
  highCutoffHz?: number;
};
const MiniAudioVisualizer = ({
  stream,
  isActive,
  className,
  widthPx = 96,
  heightPx = 24,
  mode = "single",
  lowCutoffHz,
  highCutoffHz
}: MiniAudioVisualizerProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const AudioContextCtor = useMemo(() => {
    if (typeof window === "undefined") return null;
    return window.AudioContext || (window as unknown as {
      webkitAudioContext: typeof AudioContext;
    }).webkitAudioContext || null;
  }, []);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!stream || !canvas || !AudioContextCtor) return;
    const ac: AudioContext = new AudioContextCtor();
    const source = ac.createMediaStreamSource(stream);
    const analyser = ac.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.85;
    source.connect(analyser);
    audioContextRef.current = ac;
    analyserRef.current = analyser;
    sourceRef.current = source;
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try {
        sourceRef.current?.disconnect();
      } catch {
        console.error("Error disconnecting source");
      }
      try {
        analyserRef.current?.disconnect();
      } catch {
        console.error("Error disconnecting analyser");
      }
      sourceRef.current = null;
      analyserRef.current = null;
      const ctx = audioContextRef.current;
      audioContextRef.current = null;
      if (ctx && ctx.state !== "closed") {
        ctx.close().catch(() => undefined);
      }
    };
  }, [stream, AudioContextCtor]);
  useEffect(() => {
    const canvas_0 = canvasRef.current;
    const analyser_0 = analyserRef.current;
    const ctx_0 = audioContextRef.current;
    if (!canvas_0) return;
    const c2d = canvas_0.getContext("2d");
    if (!c2d) return;
    const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
    const cssWidth = canvas_0.clientWidth || widthPx;
    const cssHeight = canvas_0.clientHeight || heightPx;
    const targetW = Math.round(cssWidth * dpr);
    const targetH = Math.round(cssHeight * dpr);
    if (canvas_0.width !== targetW || canvas_0.height !== targetH) {
      canvas_0.width = targetW;
      canvas_0.height = targetH;
    }
    const drawIdle = () => {
      c2d.clearRect(0, 0, canvas_0.width, canvas_0.height);
      const gap = 2 * dpr;
      const baseHeight = Math.max(2 * dpr, Math.round(canvas_0.height * 0.15));
      const y = Math.round((canvas_0.height - baseHeight) / 2);
      c2d.fillStyle = "rgba(148, 163, 184, 0.35)";
      if (mode === "dual") {
        const sideCount = 16;
        const barWidth = Math.max(2 * dpr, (canvas_0.width / 2 - (sideCount - 1) * gap) / sideCount);
        const centerStart = Math.round(canvas_0.width / 2 - barWidth / 2);
        // center bar (draw once, centered)
        c2d.fillRect(centerStart, y, barWidth, baseHeight);
        // right side (excluding center index)
        let xr = centerStart + barWidth + gap;
        for (let i = 1; i < sideCount; i++) {
          c2d.fillRect(xr, y, barWidth, baseHeight);
          xr += barWidth + gap;
        }
        // left side (excluding center index)
        let xl = centerStart - gap - barWidth; // start one gap away to the left
        for (let i_0 = 1; i_0 < sideCount; i_0++) {
          c2d.fillRect(xl, y, barWidth, baseHeight);
          xl -= barWidth + gap;
        }
      } else {
        const barCount = 20;
        const barWidth_0 = Math.max(2 * dpr, (canvas_0.width - (barCount - 1) * gap) / barCount);
        let x = 0;
        for (let i_1 = 0; i_1 < barCount; i_1++) {
          c2d.fillRect(x, y, barWidth_0, baseHeight);
          x += barWidth_0 + gap;
        }
      }
    };
    if (!isActive || !analyser_0 || !ctx_0) {
      drawIdle();
      return;
    }
    const dataArray = new Uint8Array(analyser_0.frequencyBinCount);
    const gap_0 = 2 * dpr;
    const isDual = mode === "dual";
    const barCount_0 = isDual ? 16 : 28; // per-side for dual
    const barWidth_1 = isDual ? Math.max(2 * dpr, (canvas_0.width / 2 - (barCount_0 - 1) * gap_0) / barCount_0) : Math.max(2 * dpr, (canvas_0.width - (barCount_0 - 1) * gap_0) / barCount_0);
    if (ctx_0.state === "suspended") {
      ctx_0.resume().catch(() => undefined);
    }
    const render = () => {
      analyser_0.getByteFrequencyData(dataArray);
      c2d.clearRect(0, 0, canvas_0.width, canvas_0.height);
      const maxBarHeight = canvas_0.height * 0.9;
      const floorHeight = canvas_0.height * 0.06;
      const primary = getComputedStyle(document.documentElement).getPropertyValue("--primary").trim() || "1 120 212";
      const primaryFg = getComputedStyle(document.documentElement).getPropertyValue("--border-secondary").trim() || "255 255 255";
      const gradient = c2d.createLinearGradient(0, 0, 0, canvas_0.height);
      gradient.addColorStop(0, `rgb(${primary.replace(/\s+/g, ", ")})`);
      gradient.addColorStop(1, `rgb(${primaryFg.replace(/\s+/g, ", ")})`);
      c2d.fillStyle = gradient;

      // Limit to selected frequency range
      const nyquist = (audioContextRef.current?.sampleRate ?? 48000) / 2;
      const lowHz = Math.max(0, Math.min(lowCutoffHz ?? 0, nyquist));
      const highHz = Math.max(0, Math.min(highCutoffHz ?? nyquist, nyquist));
      const lowBin = Math.floor(lowHz / nyquist * dataArray.length);
      const highBin = Math.max(lowBin + 1, Math.floor(highHz / nyquist * dataArray.length));
      const selectedBins = Math.max(1, highBin - lowBin);
      const binsPerBar = Math.max(1, Math.floor(selectedBins / barCount_0));
      if (isDual) {
        const centerStart_0 = Math.round(canvas_0.width / 2 - barWidth_1 / 2);
        // Draw center bar once (i=0), centered on canvas
        {
          const start = lowBin;
          const end = Math.min(start + binsPerBar, highBin);
          let sum = 0;
          for (let j = start; j < end; j++) sum += dataArray[j];
          const avg = sum / (end - start);
          const amp = avg / 255;
          const barHeight = Math.max(floorHeight, amp * maxBarHeight);
          const y_0 = (canvas_0.height - barHeight) / 2;
          c2d.fillRect(centerStart_0, y_0, barWidth_1, barHeight);
        }
        // Right side (i >= 1)
        let xr_0 = centerStart_0 + barWidth_1 + gap_0;
        for (let i_2 = 1; i_2 < barCount_0; i_2++) {
          const start_0 = lowBin + i_2 * binsPerBar;
          const end_0 = Math.min(start_0 + binsPerBar, highBin);
          let sum_0 = 0;
          for (let j_0 = start_0; j_0 < end_0; j_0++) sum_0 += dataArray[j_0];
          const avg_0 = sum_0 / (end_0 - start_0);
          const amp_0 = avg_0 / 255;
          const barHeight_0 = Math.max(floorHeight, amp_0 * maxBarHeight);
          const y_1 = (canvas_0.height - barHeight_0) / 2;
          c2d.fillRect(xr_0, y_1, barWidth_1, barHeight_0);
          xr_0 += barWidth_1 + gap_0;
        }
        // Left side mirrored (i >= 1)
        let xl_0 = centerStart_0 - gap_0 - barWidth_1;
        for (let i_3 = 1; i_3 < barCount_0; i_3++) {
          const start_1 = lowBin + i_3 * binsPerBar;
          const end_1 = Math.min(start_1 + binsPerBar, highBin);
          let sum_1 = 0;
          for (let j_1 = start_1; j_1 < end_1; j_1++) sum_1 += dataArray[j_1];
          const avg_1 = sum_1 / (end_1 - start_1);
          const amp_1 = avg_1 / 255;
          const barHeight_1 = Math.max(floorHeight, amp_1 * maxBarHeight);
          const y_2 = (canvas_0.height - barHeight_1) / 2;
          c2d.fillRect(xl_0, y_2, barWidth_1, barHeight_1);
          xl_0 -= barWidth_1 + gap_0;
        }
      } else {
        // Single from left to right
        let x_0 = 0;
        for (let i_4 = 0; i_4 < barCount_0; i_4++) {
          const start_2 = lowBin + i_4 * binsPerBar;
          const end_2 = Math.min(start_2 + binsPerBar, highBin);
          let sum_2 = 0;
          for (let j_2 = start_2; j_2 < end_2; j_2++) sum_2 += dataArray[j_2];
          const avg_2 = sum_2 / (end_2 - start_2);
          const amp_2 = avg_2 / 255;
          const barHeight_2 = Math.max(floorHeight, amp_2 * maxBarHeight);
          const y_3 = (canvas_0.height - barHeight_2) / 2;
          c2d.fillRect(x_0, y_3, barWidth_1, barHeight_2);
          x_0 += barWidth_1 + gap_0;
        }
      }
      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (audioContextRef.current && audioContextRef.current.state === "running") {
        audioContextRef.current.suspend().catch(() => undefined);
      }
    };
  }, [isActive, stream, widthPx, heightPx, mode, lowCutoffHz, highCutoffHz]);
  return <canvas ref={canvasRef} className={className} aria-hidden style={{
    width: widthPx,
    height: heightPx
  }} />;
};
export default MiniAudioVisualizer;